import { getDeserializedFlow } from "@/services-handlers/getDeserializedFlow"
import FlowUseCases, { FlowType } from "@/flows/useCases"
import { loadFlowRaw } from "./loadFlowRaw"
import { computed, Ref, ref } from "vue"
import { IFlowRaw } from "./model/flowRaw"
import IFlowConfig from "./IFlowConfig"
import { IStepAction } from "./model/flow.types"
import { IFlowValues } from "./model/flowValues.types"

// Composable acting as flow store, managing its state and actions

const useRouteManager = () => {
  // Reactive state
  const currentStepId: Ref<string | undefined> = ref()
  const longestRouteTraversed: Ref<string[]> = ref([])

  // Getters

  // Returns steps visited up to the current steps
  const route = computed(() => {
    if (!currentStepId.value) return []
    const fullRoute = longestRouteTraversed.value
    return fullRoute.slice(0, fullRoute.indexOf(currentStepId.value) + 1)
  })

  const canRevisitNext = computed(
    () =>
      route.value && longestRouteTraversed.value.length > route.value.length,
  )

  // Actions
  const setFirstStep = (stepId: string) => {
    currentStepId.value = stepId
    longestRouteTraversed.value = [stepId]
  }

  const goToStep = (stepId: string) => {
    if (longestRouteTraversed.value.at(-1) === currentStepId.value) {
      longestRouteTraversed.value.push(stepId)
    } else if (!longestRouteTraversed.value.includes(stepId)) {
      longestRouteTraversed.value = route.value
      longestRouteTraversed.value.push(stepId)
    }
    currentStepId.value = stepId
  }

  const previous = () => {
    currentStepId.value = route.value.at(-2)
  }

  const revisitNext = () => {
    currentStepId.value = longestRouteTraversed.value[route.value.length]
  }

  return {
    currentStepId,
    route,
    canRevisitNext,
    revisitNext,
    goToStep,
    previous,
    setFirstStep,
  }
}

export const useFlowManager = (startStep: string) => {
  const routeManager = useRouteManager()

  // Private, non-reactive properties
  let rawFlow: IFlowRaw
  let flowConfig: IFlowConfig

  // Reactive properties
  const isLoaded = ref(false)

  // Flow state
  const values: Ref<IFlowValues> = ref({})
  const actionError = ref("")

  // Getters
  const currentStep = computed(() =>
    steps.value
      ? steps.value.find((step) => step.id === routeManager.currentStepId.value)
      : null,
  )

  const steps = computed(() => {
    return flow.value?.data?.steps
  })

  const flow = computed(() => {
    if (!isLoaded.value) return null
    const flowAggregate = getDeserializedFlow(rawFlow, flowConfig, values.value)

    if (!routeManager.currentStepId.value) {
      routeManager.setFirstStep(startStep || flowAggregate.data.steps[0].id)
    }

    return flowAggregate
  })

  const currentProgressPercentage = computed(() => {
    const memo = [...routeManager.route.value]

    const getPossibleNextSteps = (action: IStepAction) => {
      const possibleNextSteps: string[] = []

      if (action.nextIf) {
        possibleNextSteps.push(...action.nextIf.map((action) => action.step))
      }

      if (action.nextId) {
        possibleNextSteps.push(...action.nextId)
      }

      const unvisitedNextSteps = possibleNextSteps
        .map((stepId) => steps.value?.find((step) => step.id === stepId))
        .filter((step) => step !== undefined && !memo.includes(step.id))

      return unvisitedNextSteps
    }

    const findLongestRouteLeft = (step = currentStep.value, depth = 0) => {
      const lengths = [0]

      if (step?.actions) {
        for (const action of step.actions) {
          const nextSteps = getPossibleNextSteps(action)

          if (nextSteps.length) {
            nextSteps.forEach((step) => {
              lengths.push(findLongestRouteLeft(step, depth + 1))
            })
          } else {
            lengths.push(depth)
          }
        }
      }
      return Math.max(...lengths)
    }
    return ((memo.length - 1) / (memo.length + findLongestRouteLeft())) * 100
  })

  // Actions

  // `isLoaded` cannot guard re-entry on its own: it is not set until after the
  // awaits below, so two calls arriving in that window both pass it and the
  // slower one's `values` assignment discards everything written since it
  // started — including a step's own emitted input (SO-32286). Hold the
  // in-flight promise so the second caller awaits the first load instead of
  // starting a competing one.
  let loadInFlight: Promise<void> | null = null

  function setFlow(workflowId: FlowType) {
    if (isLoaded.value) return Promise.resolve()

    loadInFlight ??= loadFlow(workflowId).finally(() => {
      loadInFlight = null
    })

    return loadInFlight
  }

  async function loadFlow(workflowId: FlowType) {
    const [flowRawDefinition, flowConfigModule] = await Promise.all([
      loadFlowRaw(workflowId),
      FlowUseCases[workflowId](),
    ])

    rawFlow = flowRawDefinition
    flowConfig = flowConfigModule.default as unknown as IFlowConfig
    isLoaded.value = true
  }

  async function performAction(action: IStepAction) {
    try {
      if (action.saveTo) {
        await action.saveTo(values.value)
      }

      const nextStepId = await action.getNextStep()
      if (nextStepId) {
        routeManager.goToStep(nextStepId)
        return nextStepId
      }
    } catch (error) {
      if (error instanceof Error) {
        actionError.value =
          error.message || "An error occurred while submitting the action"
        throw error
      }
    }
  }

  return {
    ...routeManager,
    currentProgressPercentage,
    currentStep,
    actionError,
    values,
    flow,
    performAction,
    setFlow,
  }
}
